import type {
  ClipDetail,
  ClipSummary,
  CopyInput,
  CopySaved,
  GrammarIssue,
  InviteLink,
  InviteRow,
  Me,
  SiteStats,
} from '@2x3/shared';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly issues: GrammarIssue[] = [],
  ) {
    super(message);
  }
}

/**
 * Same-origin fetch wrapper. In dev Vite proxies /api to the API process; in
 * production nginx does the same, so no base URL is ever needed.
 */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      ...(typeof init?.body === 'string' ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string; issues?: GrammarIssue[] } | null;
    throw new ApiError(body?.error ?? `${response.status} ${response.statusText}`, response.status, body?.issues);
  }
  return (await response.json()) as T;
}

export const api = {
  me: () => request<{ copier: Me | null }>('/api/me'),

  join: (token: string) => request<Me>('/api/join', { method: 'POST', body: JSON.stringify({ token }) }),

  leave: () => request<{ copier: null }>('/api/leave', { method: 'POST', body: '{}' }),

  setCredit: (credit: boolean) =>
    request<{ copier: Me }>('/api/me', { method: 'PATCH', body: JSON.stringify({ credit }) }),

  stats: () => request<SiteStats>('/api/stats'),

  clips: () => request<ClipSummary[]>('/api/clips'),

  clip: (id: string) => request<ClipDetail>(`/api/clips/${encodeURIComponent(id)}`),

  next: (skip?: string) =>
    request<{ id: string | null }>(`/api/next${skip ? `?skip=${encodeURIComponent(skip)}` : ''}`),

  saveCopy: (id: string, input: CopyInput) =>
    request<CopySaved>(`/api/clips/${encodeURIComponent(id)}/copy`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  admin: {
    invites: () => request<InviteRow[]>('/api/admin/invites'),

    add: (callsign: string, name: string, note: string) =>
      request<InviteLink>('/api/admin/invites', {
        method: 'POST',
        body: JSON.stringify({ callsign, name: name || null, note: note || null }),
      }),

    reissue: (callsign: string) =>
      request<InviteLink>('/api/admin/invites/reissue', { method: 'POST', body: JSON.stringify({ callsign }) }),

    revoke: (callsign: string) =>
      request<{ callsign: string; changed: boolean }>('/api/admin/invites/revoke', {
        method: 'POST',
        body: JSON.stringify({ callsign }),
      }),

    remove: (callsign: string, withCopies: boolean) =>
      request<{ callsign: string; copies: number }>('/api/admin/invites/remove', {
        method: 'POST',
        body: JSON.stringify({ callsign, withCopies }),
      }),
  },
};
